/**
 * PaymentPage Component
 *
 * Displays the user's selected plan and initiates Stripe Checkout.
 * Only accessible to authenticated users with pending_payment status.
 *
 * Project: P07 - Stripe Integration
 * Sprint: S03 - Payment Integration
 * Work Unit: WU02 - Payment Flow UI
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreditCard, Loader2, AlertCircle, Shield, Clock, ArrowLeft } from 'lucide-react';

import { Button, Card, CardBody, CardFooter, Alert } from '@enfomentis/ui';
import { AppLayout } from '@/components/shared/layouts';
import { useAuth } from '@/hooks/useAuth';
import { pendingRegistrationsRepository } from '@/repositories/pendingRegistrationsRepository';
import { Brand, Plans } from '@enfomentis/shared/config';
import type { PlanSelection } from '@/types/supabase/PendingRegistration';

// =============================================================================
// Component
// =============================================================================

export function PaymentPage(): React.JSX.Element {
  const navigate = useNavigate();
  const { authState } = useAuth();

  // State
  const [isLoading, setIsLoading] = useState(true);
  const [planSelection, setPlanSelection] = useState<PlanSelection | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);

  // Fetch plan selection from pending_registrations via repository
  const fetchPlanSelection = useCallback(async () => {
    if (!authState.user?.id) {
      // User not loaded yet or not logged in
      setIsLoading(false);
      setError('Please sign in to continue.');
      return;
    }

    try {
      const data = await pendingRegistrationsRepository.getPlanSelectionByUserId(authState.user.id);

      if (data) {
        setPlanSelection(data);
      } else {
        setError('No plan selection found. Please start registration again.');
      }
    } catch (err) {
      console.error('Error fetching plan:', err);
      setError('An unexpected error occurred.');
    } finally {
      setIsLoading(false);
    }
  }, [authState.user?.id]);

  useEffect(() => {
    // Wait for auth state to be initialized before fetching
    if (!authState.isLoading) {
      fetchPlanSelection();
    }
  }, [fetchPlanSelection, authState.isLoading]);

  // Handle checkout
  const handleCheckout = async () => {
    if (!authState.sessionToken) {
      setError('Session expired. Please log in again.');
      return;
    }

    setIsCheckoutLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${import.meta.env['VITE_SUPABASE_URL']}/functions/v1/create-checkout-session`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${authState.sessionToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({}), // Use plan from pending_registrations
        }
      );

      const result = await response.json();

      if (!response.ok || !result.success) {
        if (result.code === 'INVALID_STATUS') {
          // User might already be active
          navigate('/', { replace: true });
          return;
        }
        setError(result.error || 'Failed to create checkout session.');
        setIsCheckoutLoading(false);
        return;
      }

      // Redirect to Stripe Checkout
      window.location.href = result.checkoutUrl;
    } catch (err) {
      console.error('Checkout error:', err);
      setError('Failed to initiate checkout. Please try again.');
      setIsCheckoutLoading(false);
    }
  };

  // Get plan details
  const plan = planSelection ? Plans[planSelection.planType] : null;
  const price = plan
    ? planSelection?.billingInterval === 'year'
      ? plan.yearly
      : plan.monthly
    : null;

  // Loading state
  if (isLoading) {
    return (
      <AppLayout hideFooter>
        <Card className="items-center justify-center h-full" radius="none" shadow="none">
          <CardBody className="flex max-w-md mx-auto p-6 mt-10 text-center">
            <div className="space-y-4">
              <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary" />
              <p className="text-lg">Loading your plan...</p>
            </div>
          </CardBody>
        </Card>
      </AppLayout>
    );
  }

  // Error state (no plan found)
  // Fix #12: Redirect to /subscribe instead of /register to avoid stuck state
  // Users with existing accounts can't re-register, but they can select a plan at /subscribe
  if (error && !planSelection) {
    return (
      <AppLayout hideFooter>
        <Card className="items-center justify-center h-full" radius="none" shadow="none">
          <CardBody className="flex max-w-md mx-auto p-6 mt-10 text-center">
            <div className="space-y-4">
              <AlertCircle className="w-16 h-16 mx-auto text-danger" />
              <h1 className="text-xl font-semibold">Unable to Load Plan</h1>
              <p className="text-default-600">{error}</p>
              <Button
                color="primary"
                variant="bordered"
                onPress={() => navigate('/subscribe')}
                startContent={<ArrowLeft className="w-4 h-4" />}
              >
                Select a Plan
              </Button>
            </div>
          </CardBody>
        </Card>
      </AppLayout>
    );
  }

  // Main payment page
  return (
    <AppLayout hideFooter>
      <Card className="items-center justify-center h-full" radius="none" shadow="none">
        <CardBody className="flex max-w-lg mx-auto p-6 mt-10">
          <div className="space-y-6">
            {/* Header */}
            <div className="text-center space-y-2">
              <div className="text-primary flex justify-center mb-4">
                <CreditCard className="w-12 h-12" strokeWidth={1.5} />
              </div>
              <h1 className="text-2xl font-bold">Complete Your Subscription</h1>
              <p className="text-default-600">
                You&apos;re one step away from unlocking {Brand.displayName}
              </p>
            </div>

            {/* Plan Summary */}
            {plan && price && (
              <Card className="bg-default-50">
                <CardBody className="p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-semibold text-lg">{plan.name} Plan</h3>
                      <p className="text-sm text-default-500">
                        {planSelection?.billingInterval === 'year' ? 'Annual' : 'Monthly'} billing
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold">{price.displayPrice}</p>
                      <p className="text-sm text-default-500">
                        /{planSelection?.billingInterval === 'year' ? 'year' : 'month'}
                      </p>
                    </div>
                  </div>
                  {planSelection?.billingInterval === 'year' && price.savingsPercent && (
                    <div className="mt-3 pt-3 border-t border-default-200">
                      <p className="text-sm text-success">
                        Save {price.savingsPercent}% with annual billing
                      </p>
                    </div>
                  )}
                </CardBody>
              </Card>
            )}

            {/* Error Alert */}
            {error && (
              <Alert color="danger" variant="flat" description={error} />
            )}

            {/* Checkout Button */}
            <Button
              color="primary"
              size="lg"
              className="w-full"
              onPress={handleCheckout}
              isLoading={isCheckoutLoading}
              startContent={!isCheckoutLoading && <CreditCard className="w-5 h-5" />}
            >
              {isCheckoutLoading ? 'Redirecting to Checkout...' : 'Proceed to Payment'}
            </Button>

            {/* Trust Indicators */}
            <div className="flex flex-col gap-2 text-sm text-default-500">
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4" />
                <span>Secure payment powered by Stripe</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span>Cancel anytime, no questions asked</span>
              </div>
            </div>
          </div>
        </CardBody>

        {/* Footer */}
        <CardFooter className="max-w-lg mx-auto justify-center border-t border-default-100">
          <p className="text-sm text-default-500 text-center">
            By completing your purchase, you agree to our{' '}
            <a href="/terms" className="text-primary hover:underline">
              Terms of Service
            </a>{' '}
            and{' '}
            <a href="/privacy" className="text-primary hover:underline">
              Privacy Policy
            </a>
          </p>
        </CardFooter>
      </Card>
    </AppLayout>
  );
}
